import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  Tooltip,
  Collapse,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  FilterAlt as FilterAltIcon,
  FilterAltOff as FilterAltOffIcon,
  DateRange as DateRangeIcon,
  Save as SaveIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  SaveAs as SaveAsIcon
} from '@mui/icons-material';
import {
  DataGridPro, 
  GridToolbarContainer, 
  GridToolbarColumnsButton, 
  GridToolbarFilterButton, 
  GridToolbarDensitySelector,
  useGridApiContext,
  gridFilteredSortedRowEntriesSelector
} from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { initializeFormats, formatDate, formatAmount, formatPrice, formatQuantity } from '../../../utils/global';
import {
  CHARGEBACK_STAGE,
  CHARGEBACK_STAGE_TABS,
  DONT_FIGHT_ACTION_TYPE,
  REVERT_ACTION_TYPE,
  classifyChargebackStage,
  countChargebacksByStage,
  filterChargebacksByStage,
  formatActionTypeLabel,
  getActionType,
  getDontFightNote,
  isDontFightAction,
} from '../../../utils/chargebackStages';
import * as XLSX from 'xlsx';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import MerchantDataHubBreadcrumb from '../CreditCard/MerchantDataHubBreadcrumb';
import { readMdhNavState, formatMdhPropertyLabel, applyPropertyScope, assignedPropertyCodes } from '../CreditCard/merchantDataHubNav';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateToolbarBuFieldSx,
  harmonateToolbarButtonSx,
  harmonateSectionTitleSx,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { AuditLoggers } from '../../../utils/auditLogger';

const getHarmonateStatusChipSx = (color) => {
  const base = {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
    borderRadius: ht.buttonRadius,
    '& .MuiChip-label': { px: 1.5 },
  };
  switch (color) {
    case 'success':
      return { ...base, bgcolor: ht.secondaryTab, color: ht.text };
    case 'warning':
      return { ...base, bgcolor: ht.listBg, color: ht.text, border: `1px solid ${ht.line}` };
    case 'error':
      return { ...base, bgcolor: 'rgba(239, 63, 63, 0.12)', color: ht.red };
    case 'info':
      return { ...base, bgcolor: 'rgba(46, 166, 160, 0.12)', color: ht.text };
    default:
      return { ...base, bgcolor: ht.listBg, color: ht.text, border: `1px solid ${ht.line}` };
  }
};

/** Match CaseDetails chargeback action / history payloads (Snowflake WORKFLOW.CHARGEBACK_ACTIONS). */
const parseChargebackRecordId = (id) => {
  if (id == null || id === '') return 1;
  let num;
  if (typeof id === 'number') {
    num = Math.floor(id);
  } else if (typeof id === 'string') {
    const cleanId = id.startsWith('row-') ? id.replace('row-', '') : id;
    num = parseInt(cleanId, 10);
  } else {
    return 1;
  }
  return Math.max(1, Number.isNaN(num) ? 1 : num);
};

/** Stable key for matching list rows to pending Don't Fight / Revert patches. */
const chargebackRowKey = (row) => {
  if (!row) return '';
  if (row.ID != null && row.ID !== '') return `id:${String(row.ID)}`;
  const proc = String(row.PROCESSOR || row.processor || '').trim().toUpperCase();
  const caseNo = String(
    row.CASE_NO || row.CASE_NUMBER || row.case_no || row.case_number || ''
  )
    .trim()
    .toUpperCase();
  if (proc && caseNo) return `case:${proc}|${caseNo}`;
  if (row.id != null && row.id !== '') return `rid:${String(row.id)}`;
  return '';
};

const postChargebackActionAndHistory = async (row, actionType, notesOnAction, historyMessage) => {
  const token = localStorage.getItem('token');
  const tenantIntegration = localStorage.getItem('tenant_integration');
  const userEmail = localStorage.getItem('username') || 'unknown';

  if (!token || !tenantIntegration) {
    throw new Error('Authentication or tenant information missing');
  }

  const chargebackId =
    row.ID != null && row.ID !== ''
      ? String(row.ID)
      : row.id != null && row.id !== ''
        ? String(row.id)
        : '';
  if (!chargebackId) {
    throw new Error('Unable to determine chargeback ID for this row');
  }

  const caseNo =
    row.CASE_NO ||
    row.CASE_NUMBER ||
    row.case_no ||
    `CASE-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
  const processor =
    row.PROCESSOR ||
    row.processor ||
    row.CARD_BRAND ||
    'UNKNOWN';

  const actionPayload = {
    processor: String(processor),
    record_id: parseChargebackRecordId(row.id ?? row.ID ?? row.REFERENCE_DATA_ID),
    case_number: caseNo,
    action_type: actionType,
    action_date: new Date().toISOString(),
    user_id: userEmail,
    notes_on_fight_transaction: notesOnAction,
    created_by: userEmail,
  };

  const baseUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}`;

  const response = await fetch(`${baseUrl}/actions/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantIntegration,
    },
    body: JSON.stringify(actionPayload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to record chargeback action: ${response.statusText}`);
  }

  const historyPayload = {
    chargeback_id_fk: chargebackId,
    action: 'ACTION_UPDATE',
    status: row.STATUS || row.status || 'PENDING',
    message: historyMessage,
    created_by: userEmail,
    file_location: '',
    file_name: '',
  };

  const historyResponse = await fetch(`${baseUrl}/history/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantIntegration,
    },
    body: JSON.stringify(historyPayload),
  });

  if (!historyResponse.ok) {
    const errorData = await historyResponse.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to update chargeback history: ${historyResponse.statusText}`);
  }
};

const NORMALIZED_CHARGEBACKS_PROPERTY_STORAGE_KEY = 'normalized_chargebacks_selected_property_id';

const TIMESTAMP_DATE_COLUMNS = new Set([
  'CREATED_DATE',
  'UPDATED_DATE',
  'DATE_MODIFIED',
  'MODIFIED_DATE',
]);
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Parse API date/timestamp into a local Date at midnight for DataGrid date filtering. */
const toGridLocalDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const raw = String(value).trim();
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) return null;
  // Match formatDate() display: use local calendar day from the timestamp.
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const isDateLikeColumn = (column) => {
  const dataType = String(column.DataType || '').toLowerCase();
  if (dataType === 'date' || dataType === 'datetime' || dataType === 'timestamp') {
    return true;
  }
  const field = String(column.name || '').toLowerCase();
  return (
    field.includes('date') ||
    field.endsWith('_on') ||
    field.endsWith('_at') ||
    field.includes('time')
  );
};

const expandTimestampDateFilters = (filters) => {
  const expanded = [];

  filters.forEach((filter) => {
    const column = String(filter.column || '').toUpperCase();
    const isTimestampColumn =
      TIMESTAMP_DATE_COLUMNS.has(column) ||
      column.endsWith('_ON') ||
      column.endsWith('_AT');

    if (
      isTimestampColumn &&
      filter.operator === '=' &&
      ISO_DATE_ONLY_PATTERN.test(filter.value || '')
    ) {
      const [year, month, day] = filter.value.split('-').map(Number);
      const start = new Date(year, month - 1, day, 0, 0, 0, 0);
      const end = new Date(year, month - 1, day + 1, 0, 0, 0, 0);
      expanded.push(
        { ...filter, id: `${filter.id}_start`, operator: '>=', value: start.toISOString() },
        { ...filter, id: `${filter.id}_end`, operator: '<', value: end.toISOString() }
      );
      return;
    }

    expanded.push(filter);
  });

  return expanded;
};

const getTenantPropertyOptionId = (option) =>
  option?.propertyId ?? option?.PROPERTY_ID ?? option?.property_id ?? null;

/** Snowflake PROPERTY_ID = tenant property code (prefix before " - " in dropdown label). */
function propertyCodeForApi(prop) {
  if (!prop) {
    return null;
  }
  const explicit = prop.propertyCode ?? prop.PROPERTY_CODE;
  if (explicit != null && String(explicit).trim()) {
    return String(explicit).trim();
  }

  const label = prop.propertyName ?? prop.PROPERTY_NAME;
  if (label == null || !String(label).trim()) {
    return null;
  }

  const full = String(label).trim();
  const sep = ' - ';
  const i = full.indexOf(sep);
  if (i > 0) {
    return full.slice(0, i).trim();
  }

  return full;
}

const StageTabLabel = ({ text, count, tone = 'default' }) => {
  const tones = {
    default: { bg: '#f0f0f0', color: '#616161' },
    success: { bg: '#e8f5e9', color: '#2e7d32' },
    info: { bg: '#e3f2fd', color: '#1565c0' },
    warning: { bg: '#fff3e0', color: '#e65100' },
  };
  const { bg, color } = tones[tone] || tones.default;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {text}
      <Box
        component="span"
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: 11,
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: '22px',
          textAlign: 'center',
          bgcolor: bg,
          color,
        }}
      >
        {count}
      </Box>
    </Box>
  );
};

const Charge = () => {
  const { canExport } = useFunctionPermissions(FC.MERCHANT_DATA_HUB);
  const navigate = useNavigate();
  const { chargeId } = useParams();
  const [searchParams] = useSearchParams();
  const mdhNav = useMemo(() => readMdhNavState(searchParams), [searchParams]);
  const propertyLockedFromHub = mdhNav.propertyLocked;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const open = Boolean(anchorEl);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertiesReady, setPropertiesReady] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  /** Property code (Snowflake PROPERTY_ID) last applied via Load Data. */
  const [appliedPropertyId, setAppliedPropertyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const assignedCodes = useMemo(() => assignedPropertyCodes(properties), [properties]);
  const initialChargebackFetchDone = useRef(false);
  const [rows, setRows] = useState([]);
  const [stageTab, setStageTab] = useState(CHARGEBACK_STAGE.ALL);
  const [columns, setColumns] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [gridConfig, setGridConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [formatsInitialized, setFormatsInitialized] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 1000;
  const [hasMore, setHasMore] = useState(true);
  /** Keep Don't Fight / Revert visible if API refetch lags behind CHARGEBACK_ACTIONS. */
  const pendingActionPatchesRef = useRef(new Map());

  const stageCounts = useMemo(() => countChargebacksByStage(rows), [rows]);
  const stageFilteredRows = useMemo(
    () => filterChargebacksByStage(rows, stageTab),
    [rows, stageTab]
  );
  const activeStageTabIndex = Math.max(
    0,
    CHARGEBACK_STAGE_TABS.findIndex((tab) => tab.key === stageTab)
  );

  const selectedRowIsRejected =
    !!selectedRow &&
    (classifyChargebackStage(selectedRow) === CHARGEBACK_STAGE.REJECTED ||
      isDontFightAction(getActionType(selectedRow)));

  const selectedRowIsExpired =
    !!selectedRow && classifyChargebackStage(selectedRow) === CHARGEBACK_STAGE.EXPIRED;

  /** Columns for the active stage (Rejected adds Note). */
  const displayColumns = useMemo(() => {
    if (!columns.length) return columns;
    if (stageTab !== CHARGEBACK_STAGE.REJECTED) return columns;

    const noteField = 'NOTES_ON_FIGHT_TRANSACTION';
    const alreadyHasNote = columns.some(
      (c) => String(c.field || '').toUpperCase() === noteField
    );
    if (alreadyHasNote) return columns;

    const noteColumn = {
      field: noteField,
      headerName: 'Note',
      flex: 1,
      minWidth: 220,
      sortable: true,
      filterable: true,
      valueGetter: (params) => getDontFightNote(params.row),
      renderCell: (params) => {
        const note = getDontFightNote(params.row);
        if (!note) return '-';
        return (
          <Tooltip title={note}>
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                maxWidth: '100%',
              }}
            >
              {note}
            </Box>
          </Tooltip>
        );
      },
    };

    // After Actions + Action Type when present
    const actionTypeIdx = columns.findIndex(
      (c) => String(c.field || '').toUpperCase() === 'ACTION_TYPE'
    );
    if (actionTypeIdx >= 0) {
      return [
        ...columns.slice(0, actionTypeIdx + 1),
        noteColumn,
        ...columns.slice(actionTypeIdx + 1),
      ];
    }
    if (columns[0]?.field === 'actions') {
      return [columns[0], noteColumn, ...columns.slice(1)];
    }
    return [noteColumn, ...columns];
  }, [columns, stageTab]);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('');
  
  // Custom filter states
  const [customFilters, setCustomFilters] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [showCustomFilters, setShowCustomFilters] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);
  
  // Export states
  const [downloading, setDownloading] = useState(false);

  // Delete confirmation states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);

  // Don't Fight confirmation (note + move to Rejected)
  const [dontFightDialogOpen, setDontFightDialogOpen] = useState(false);
  const [dontFightRow, setDontFightRow] = useState(null);
  const [dontFightNote, setDontFightNote] = useState('');
  const [dontFightSubmitting, setDontFightSubmitting] = useState(false);

  // Revert Don't Fight confirmation
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertRow, setRevertRow] = useState(null);
  const [revertSubmitting, setRevertSubmitting] = useState(false);

  const viewName = 'CHARGEBACK';

  // Filter operators
  const FILTER_OPERATORS = [
    { value: '=', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: '>', label: 'greater than' },
    { value: '>=', label: 'greater than or equal' },
    { value: '<', label: 'less than' },
    { value: '<=', label: 'less than or equal' },
    { value: 'contains', label: 'contains' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'endsWith', label: 'ends with' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ];

  // Merchant Data Hub → Normalized Chargebacks → View All (and direct /chargeback routes)
  useEffect(() => {
    const logViewAllChargebacks = async () => {
      try {
        await AuditLoggers.chargebackViewAll();
      } catch (error) {
        console.error('Failed to log chargeback view:', error);
      }
    };

    logViewAllChargebacks();
  }, []);

  // Same user–tenant properties source as Workforce Operations
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setPropertiesLoading(true);
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        const userId = localStorage.getItem('user_id');

        if (!token || !tenantId || !userId) {
          setProperties([]);
          setSelectedProperty(null);
          return;
        }

        const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
        const resp = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!resp.ok) {
          throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
        }

        const data = await resp.json();
        const list = Array.isArray(data) ? data : [];
        setProperties(list);

        // From Merchant Data Hub with a property → lock that property (no selector)
        if (mdhNav.propertyLocked && mdhNav.propertyCode) {
          const match = list.find(
            (p) => String(propertyCodeForApi(p) ?? '') === String(mdhNav.propertyCode)
          );
          if (match) setSelectedProperty(match);
          setAppliedPropertyId(mdhNav.propertyCode);
          return;
        }

        // From hub with no property selected: do not restore a saved property.
        // The table is limited to this user's assigned properties.
        if (mdhNav.fromHub && !mdhNav.propertyCode) {
          setSelectedProperty(null);
          setAppliedPropertyId(null);
          localStorage.removeItem(NORMALIZED_CHARGEBACKS_PROPERTY_STORAGE_KEY);
          return;
        }

        const savedId = localStorage.getItem(NORMALIZED_CHARGEBACKS_PROPERTY_STORAGE_KEY);
        if (savedId && list.length > 0) {
          const match = list.find((p) => String(getTenantPropertyOptionId(p) ?? '') === savedId);
          if (match) {
            setSelectedProperty(match);
          }
        }
      } catch (e) {
        console.error('Error fetching tenant properties for chargebacks:', e);
        setProperties([]);
      } finally {
        setPropertiesLoading(false);
        setPropertiesReady(true);
      }
    };

    fetchProperties();
  }, [mdhNav.fromHub, mdhNav.propertyCode, mdhNav.propertyLocked]);

  const handlePropertySelectChange = (event, newValue) => {
    setSelectedProperty(newValue);
    const id = getTenantPropertyOptionId(newValue);
    if (id != null && id !== '') {
      localStorage.setItem(NORMALIZED_CHARGEBACKS_PROPERTY_STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(NORMALIZED_CHARGEBACKS_PROPERTY_STORAGE_KEY);
    }
  };

  // Get available columns for filtering
  const getAvailableColumns = () => {
    if (columns.length > 0) {
      return columns.map(col => ({
        value: col.field,
        label: col.headerName || col.field
      }));
    }
    return [];
  };

  // Fetch grid configuration from the backend
  const fetchGridConfig = async (viewName) => {
    if (!viewName) return null;
    
    setLoadingConfig(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${encodeURIComponent(viewName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch grid configuration: ${response.statusText}`);
      }

      const config = await response.json();
      return config;
    } catch (error) {
      console.error('Error fetching grid configuration:', error);
      return null;
    } finally {
      setLoadingConfig(false);
    }
  };

  // Handle menu open
  const handleMenuClick = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const sameChargebackRow = (a, b) => {
    if (!a || !b) return false;
    if (a.ID != null && b.ID != null && String(a.ID) === String(b.ID)) return true;
    if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true;
    const aCase = String(a.CASE_NO || a.CASE_NUMBER || a.case_no || '').trim().toUpperCase();
    const bCase = String(b.CASE_NO || b.CASE_NUMBER || b.case_no || '').trim().toUpperCase();
    const aProc = String(a.PROCESSOR || a.processor || '').trim().toUpperCase();
    const bProc = String(b.PROCESSOR || b.processor || '').trim().toUpperCase();
    return !!aCase && !!bCase && aCase === bCase && aProc === bProc;
  };

  const rememberPendingActionPatch = (row, patch) => {
    const key = chargebackRowKey(row);
    if (!key) return;
    pendingActionPatchesRef.current.set(key, {
      ACTION_TYPE: patch.ACTION_TYPE,
      NOTES_ON_FIGHT_TRANSACTION: patch.NOTES_ON_FIGHT_TRANSACTION ?? null,
    });
  };

  const normalizePendingAction = (value) =>
    String(value ?? '')
      .trim()
      .toUpperCase();

  const applyPendingPatchesToRows = (list) => {
    const patches = pendingActionPatchesRef.current;
    if (!patches.size) return list;

    return list.map((row) => {
      const key = chargebackRowKey(row);
      if (!key || !patches.has(key)) return row;

      const patch = patches.get(key);
      const serverAction = getActionType(row);
      const wantDontFight = isDontFightAction(patch.ACTION_TYPE);
      const wantCleared =
        patch.ACTION_TYPE == null ||
        patch.ACTION_TYPE === '' ||
        normalizePendingAction(patch.ACTION_TYPE) === REVERT_ACTION_TYPE;

      if (wantDontFight && isDontFightAction(serverAction)) {
        const serverNote = getDontFightNote(row);
        if (!serverNote && patch.NOTES_ON_FIGHT_TRANSACTION) {
          patches.delete(key);
          return {
            ...row,
            NOTES_ON_FIGHT_TRANSACTION: patch.NOTES_ON_FIGHT_TRANSACTION,
          };
        }
        patches.delete(key);
        return row;
      }

      if (wantCleared && !serverAction) {
        patches.delete(key);
        return row;
      }

      // API still lagging — keep optimistic ACTION_TYPE / note so Rejected count stays correct.
      return {
        ...row,
        ACTION_TYPE: patch.ACTION_TYPE,
        NOTES_ON_FIGHT_TRANSACTION: patch.NOTES_ON_FIGHT_TRANSACTION,
      };
    });
  };

  const patchLocalChargebackRow = (targetRow, patch) => {
    rememberPendingActionPatch(targetRow, patch);
    setRows((prev) =>
      prev.map((row) => (sameChargebackRow(row, targetRow) ? { ...row, ...patch } : row))
    );
  };

  const openDontFightDialog = (row) => {
    setDontFightRow(row);
    setDontFightNote('');
    setDontFightDialogOpen(true);
  };

  const closeDontFightDialog = () => {
    if (dontFightSubmitting) return;
    setDontFightDialogOpen(false);
    setDontFightRow(null);
    setDontFightNote('');
  };

  const confirmDontFight = async () => {
    if (!dontFightRow) return;
    const row = dontFightRow;
    const note = dontFightNote.trim();
    setDontFightSubmitting(true);
    try {
      await postChargebackActionAndHistory(
        row,
        DONT_FIGHT_ACTION_TYPE,
        note || null,
        note
          ? `Don't Fight selected: ${note}`
          : "Don't Fight selected"
      );
      // Instant UI move (do not wait for a full page refresh).
      patchLocalChargebackRow(row, {
        ACTION_TYPE: DONT_FIGHT_ACTION_TYPE,
        NOTES_ON_FIGHT_TRANSACTION: note || null,
      });
      setStageTab(CHARGEBACK_STAGE.REJECTED);
      setDontFightDialogOpen(false);
      setDontFightRow(null);
      setDontFightNote('');
      setSnackbar({
        open: true,
        message: "Don't Fight confirmed — chargeback moved to Rejected.",
        severity: 'success',
      });
      // Do not refetch here. A follow-up load replaces the 1000-row window and
      // the stage counts jump (and the row can drop out of the loaded set).
    } catch (err) {
      console.error("Don't Fight chargeback record failed:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to record Don't Fight. Please try again.",
        severity: 'error',
      });
    } finally {
      setDontFightSubmitting(false);
      setSelectedRow(null);
    }
  };

  const openRevertDialog = (row) => {
    setRevertRow(row);
    setRevertDialogOpen(true);
  };

  const closeRevertDialog = () => {
    if (revertSubmitting) return;
    setRevertDialogOpen(false);
    setRevertRow(null);
  };

  const confirmRevertDontFight = async () => {
    if (!revertRow) return;
    const row = revertRow;
    setRevertSubmitting(true);
    try {
      await postChargebackActionAndHistory(
        row,
        REVERT_ACTION_TYPE,
        'Reverted from Rejected back to main list',
        "Don't Fight reverted — chargeback returned to main list"
      );
      patchLocalChargebackRow(row, {
        ACTION_TYPE: null,
        NOTES_ON_FIGHT_TRANSACTION: null,
      });
      setStageTab(CHARGEBACK_STAGE.ALL);
      setRevertDialogOpen(false);
      setRevertRow(null);
      setSnackbar({
        open: true,
        message: 'Chargeback reverted to the main list.',
        severity: 'success',
      });
    } catch (err) {
      console.error('Revert Don\'t Fight failed:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to revert chargeback. Please try again.',
        severity: 'error',
      });
    } finally {
      setRevertSubmitting(false);
      setSelectedRow(null);
    }
  };

  // Handle menu item click
  const handleMenuItemClick = async (action) => {
    if (action === 'fight' && selectedRow) {
      if (classifyChargebackStage(selectedRow) === CHARGEBACK_STAGE.EXPIRED) {
        handleMenuClose();
        setSnackbar({
          open: true,
          message: 'This chargeback is expired. The respond-by date has passed.',
          severity: 'info',
        });
        return;
      }

      // Rejected rows cannot start a fight — use Revert first.
      if (
        classifyChargebackStage(selectedRow) === CHARGEBACK_STAGE.REJECTED ||
        isDontFightAction(getActionType(selectedRow))
      ) {
        handleMenuClose();
        setSnackbar({
          open: true,
          message: 'Fight is not available from Rejected. Revert the chargeback first.',
          severity: 'info',
        });
        return;
      }

      // Use ID field for navigation as per the API response
      const recordId = selectedRow.ID || 'UNKNOWN';

      // Pass the entire row data as state to the route
      navigate(`/chargeback/case/${encodeURIComponent(recordId)}`, {
        state: {
          caseData: {
            ...selectedRow,
            // Ensure we have required arrays in the data
            history: Array.isArray(selectedRow.history) ? selectedRow.history : [
              {
                date: new Date().toISOString().split('T')[0],
                status: 'Case Opened',
                description: 'Case was created from chargeback list',
              },
            ],
            documents: Array.isArray(selectedRow.documents) ? selectedRow.documents : [],
            notes: Array.isArray(selectedRow.notes) ? selectedRow.notes : [],
          },
          selectedProperty: selectedProperty || null,
        },
      });
      handleMenuClose();
      return;
    }

    if (action === 'noAction' && selectedRow) {
      if (classifyChargebackStage(selectedRow) === CHARGEBACK_STAGE.EXPIRED) {
        handleMenuClose();
        setSnackbar({
          open: true,
          message: 'This chargeback is expired. The respond-by date has passed.',
          severity: 'info',
        });
        return;
      }

      const row = selectedRow;
      handleMenuClose();
      openDontFightDialog(row);
      setSelectedRow(null);
      return;
    }

    if (action === 'revert' && selectedRow) {
      const row = selectedRow;
      handleMenuClose();
      openRevertDialog(row);
      setSelectedRow(null);
      return;
    }

    handleMenuClose();
  };

  // Generate action column
  const renderActionButton = (params) => {
    return (
      <Box>
        <IconButton
          size="small"
          sx={harmonateGridActionIconSx}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClick(e, params.row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
    );
  };

  // Generate columns from configuration using global formatters
  const generateColumnsFromConfig = (config) => {
    if (!config || !config.grid || !config.grid.columns) return [];
    
    // Add action column as first column
    const actionColumn = {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: renderActionButton,
    };
    
    // Only show columns the API marks as visible (omit visible: false; missing visible defaults to shown)
    const visibleConfigColumns = config.grid.columns.filter(
      (column) => column.visible !== false
    );

    const dataColumns = visibleConfigColumns.map((column) => {
      const dataType = column.DataType?.toLowerCase() || 'string';
      const isDate = isDateLikeColumn(column);
      const isNumber = dataType === 'number' || dataType === 'integer' || dataType === 'float';
      const isAmount = column.name.toLowerCase().includes('amount') || 
                      column.name.toLowerCase().includes('total') || 
                      column.name.toLowerCase().includes('price');
      const isQuantity = column.name.toLowerCase().includes('quantity') || 
                        column.name.toLowerCase().includes('qty') ||
                        column.name.toLowerCase().includes('count');

      let columnType = 'string';
      if (isNumber) columnType = 'number';
      else if (isDate) columnType = 'date';
      
      return {
        field: column.name,
        headerName: column.title || column.name,
        type: columnType,
        width: column.width || 150,
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const value = params.row?.[column.name] ?? params.value;
          if (String(column.name).toUpperCase() === 'ACTION_TYPE') {
            return formatActionTypeLabel(value);
          }
          if (isDate) {
            return toGridLocalDate(value);
          }
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return value;
        },
        valueFormatter: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === '') return '';
          if (String(column.name).toUpperCase() === 'ACTION_TYPE') {
            return formatActionTypeLabel(value);
          }
          
          try {
            if (isDate) {
              // valueGetter already normalized to local calendar day.
              return formatDate(value);
            } else if (isNumber) {
              const numValue = parseFloat(value);
              if (isNaN(numValue)) return value;
              
              // Use the appropriate formatting function based on column type and name
              if (isAmount) return formatAmount(numValue);
              if (isQuantity) return formatQuantity(numValue);
              return formatPrice(numValue);
            }
          } catch (e) {
            console.error(`Error formatting value for ${column.name}:`, e);
            return value; // Return original value on error
          }
          
          return String(value);
        },
        renderCell: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === '') return '-';
          
          // Special handling for status-like columns
          const statusColumns = ['status', 'state', 'chargeback_status', 'win_loss'];
          if (statusColumns.some(statusCol => column.name.toLowerCase().includes(statusCol))) {
            const statusColors = {
              completed: 'success',
              pending: 'warning',
              failed: 'error',
              won: 'success',
              lost: 'error',
              approved: 'success',
              declined: 'error',
              open: 'info',
              closed: 'default',
              in_progress: 'warning',
              resolved: 'success'
            };
            
            const color = statusColors[String(value).toLowerCase()] || 'default';
            
            return (
              <Chip
                label={value}
                size="small"
                sx={getHarmonateStatusChipSx(color)}
              />
            );
          }
          
          // For date and number fields, use the formatted value from valueFormatter
          if (isDate || isNumber) {
            return params.formattedValue || value;
          }
          
          return String(value);
        }
      };
    });

    // Put ACTION_TYPE right after Actions and before Processor (and all other fields).
    const actionTypeCol = dataColumns.find(
      (c) => String(c.field || '').toUpperCase() === 'ACTION_TYPE'
    );
    const restColumns = dataColumns.filter(
      (c) => String(c.field || '').toUpperCase() !== 'ACTION_TYPE'
    );

    return [actionColumn, ...(actionTypeCol ? [actionTypeCol] : []), ...restColumns];
  };

  // Load saved filters
  const loadSavedFilters = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers?tenantId=${tenantId}&report=NORMALIZED_CHARGEBACKS`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSavedFilters(data);
      } else {
        throw new Error('Failed to load saved filters');
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load saved filters',
        severity: 'error'
      });
    }
  }, []);

  // Fetch saved filters for the current report
  const fetchSavedFilters = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const reportName = 'NORMALIZED_CHARGEBACKS';
     
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers?tenantId=${tenantId}&report=${reportName}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch saved filters');
      }
      const data = await response.json();
      setSavedFilters(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load saved filters',
        severity: 'error'
      });
      setSavedFilters([]);
    }
  };

  // Handle delete confirmation dialog open
  const handleDeleteClick = (filterId) => {
    setFilterToDelete(filterId);
    setDeleteConfirmOpen(true);
  };

  // Handle delete confirmation dialog close
  const handleDeleteClose = () => {
    setDeleteConfirmOpen(false);
    setFilterToDelete(null);
  };

  // Delete a saved filter
  const handleDeleteFilter = async () => {
    if (!filterToDelete) {
      setSnackbar({
        open: true,
        message: 'No filter selected for deletion',
        severity: 'error',
        autoHideDuration: 3000
      });
      handleDeleteClose();
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers/${filterToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete filter');
      }
      // Refresh the filters list
      await loadSavedFilters();
     
      // Clear selection if the deleted filter was selected
      if (selectedFilter && (selectedFilter.id === filterToDelete || selectedFilter === filterToDelete)) {
        setSelectedFilter('');
        clearCustomFilters();
      }
      setSnackbar({
        open: true,
        message: 'Filter deleted successfully',
        severity: 'success',
        autoHideDuration: 3000
      });
    } catch (error) {
      console.error('Error deleting filter:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete filter',
        severity: 'error',
        autoHideDuration: 3000
      });
    } finally {
      handleDeleteClose();
    }
  };

  // Apply a saved filter
  const applySavedFilter = (filterData) => {
    try {
      // If filterData is empty, clear the selection
      if (!filterData) {
        setSelectedFilter('');
        setFilterName('');
        return;
      }
     
      const filter = typeof filterData.filter === 'string'
        ? JSON.parse(filterData.filter)
        : filterData.filter;
       
      // Set the selected filter and its name
      setSelectedFilter(filterData);
      setFilterName(filterData.filterName || '');
     
      // Convert date strings to Date objects if they exist
      const dateRangeFilter = filter.dateRange ? {
        start: filter.dateRange.start ? new Date(filter.dateRange.start) : null,
        end: filter.dateRange.end ? new Date(filter.dateRange.end) : null
      } : { start: null, end: null };
     
      // Apply date range
      setDateRange(dateRangeFilter);
     
      // Apply custom filters if they exist
      if (Array.isArray(filter.customFilters) && filter.customFilters.length > 0) {
        setCustomFilters(filter.customFilters);
        setShowCustomFilters(true);
      } else {
        setCustomFilters([]);
        setShowCustomFilters(!!(dateRangeFilter.start || dateRangeFilter.end));
      }
     
      // Apply any applied filters
      setAppliedFilters(filter.customFilters || []);
    } catch (error) {
      console.error('Error applying filter:', error);
      setSnackbar({
        open: true,
        message: 'Failed to apply filter. The filter format may be invalid.',
        severity: 'error'
      });
    }
  };

  // Handle opening the save filter dialog
  const handleOpenSaveDialog = () => {
    // If we have a selected filter, set its name
    if (selectedFilter && typeof selectedFilter === 'object') {
      setFilterName(selectedFilter.filterName || '');
    } else {
      setFilterName('');
    }
    setSaveDialogOpen(true);
  };

  // Handle saving filter (save as new or update existing)
  const handleSaveFilter = async (isUpdate = false) => {
    if (!filterName.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a name for the filter',
        severity: 'error'
      });
      return;
    }
   
    // If it's an update but no filter is selected, treat as new filter
    if (isUpdate && !selectedFilter) {
      isUpdate = false;
    }
    setSavingFilter(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const reportName = 'NORMALIZED_CHARGEBACKS';
     
      // Process filters the same way as in loadData
      const validFilters = customFilters.length > 0 
        ? customFilters.filter(f => f.column && f.operator && f.value)
        : appliedFilters;
      
      // Update applied filters if we have custom filters
      if (customFilters.length > 0) {
        setAppliedFilters(validFilters);
      }

      const filterData = {
        tenantId,
        report: reportName,
        filterName: filterName.trim(),
        filter: JSON.stringify({
          dateRange,
          customFilters: validFilters,
          appliedFilters: validFilters // Also save the processed filters
        })
      };
      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers`;
      const method = isUpdate ? 'PUT' : 'POST';
     
      if (isUpdate && selectedFilter) {
        // For update, use the format: /conductor/chargeback-headers/{id}
        const filterId = typeof selectedFilter === 'string' ? selectedFilter : selectedFilter.id;
        if (!filterId) {
          throw new Error('Cannot update filter: No filter ID provided');
        }
        url = `${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers/${filterId}`;
      } else {
        // For new filter, use the base URL
        url = `${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers`;
      }
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(filterData)
      });
      if (!response.ok) {
        throw new Error(`Failed to ${isUpdate ? 'update' : 'save'} filter`);
      }
      setSnackbar({
        open: true,
        message: `Filter ${isUpdate ? 'updated' : 'saved'} successfully`,
        severity: 'success'
      });
     
      setSaveDialogOpen(false);
      setFilterName('');
      setSelectedFilter('');
     
      // Refresh the saved filters list
      await loadSavedFilters();
    } catch (error) {
      console.error(`Error ${isUpdate ? 'updating' : 'saving'} filter:`, error);
      setSnackbar({
        open: true,
        message: `Failed to ${isUpdate ? 'update' : 'save'} filter. Please try again.`,
        severity: 'error'
      });
    } finally {
      setSavingFilter(false);
    }
  };

  // Initialize formats and load saved filters when component mounts
  useEffect(() => {
    const initFormats = async () => {
      const tenantId = localStorage.getItem('tenant_id');
      if (tenantId) {
        try {
          await initializeFormats(tenantId);
          setFormatsInitialized(true);
          await loadSavedFilters();
        } catch (error) {
          console.error('Error initializing formats:', error);
          setFormatsInitialized(true); // Continue with default formats
        }
      } else {
        console.warn('No tenant ID found, using default formats');
        setFormatsInitialized(true);
      }
    };

    initFormats();
  }, [loadSavedFilters]);

  // Fetch once assignments are known, unless the hub already locked one property.
  useEffect(() => {
    if (!formatsInitialized) return;
    if (mdhNav.propertyLocked && mdhNav.propertyCode) {
      if (initialChargebackFetchDone.current) return;
      initialChargebackFetchDone.current = true;
      setAppliedPropertyId(mdhNav.propertyCode);
      fetchData([], mdhNav.propertyCode);
      return;
    }
    if (!propertiesReady || initialChargebackFetchDone.current) return;
    initialChargebackFetchDone.current = true;
    setAppliedPropertyId(null);
    fetchData([], null);
  }, [formatsInitialized, propertiesReady, mdhNav.propertyLocked, mdhNav.propertyCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle pagination
  useEffect(() => {
    if (currentPage > 1) {
      fetchData();
    }
  }, [currentPage]);

  // Fetch report data
  const fetchData = async (overrideFilters = null, overridePropertyId = undefined, options = {}) => {
    const { silent = false } = options || {};
    // Silent sync after Don't Fight/Revert must not flash Loading or wipe optimistic rows.
    if (!silent) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!tenantIntegrationId) {
        throw new Error('Tenant integration ID not found');
      }

      // Determine which filters to use for this fetch. If an override is provided
      // (e.g., right after the user clicks "Load Data"), prefer that to avoid
      // waiting for async state updates.
      let filtersToUse = Array.isArray(overrideFilters) ? overrideFilters : appliedFilters;

      // If nothing has been applied yet but custom filters exist, fall back to them.
      if ((!filtersToUse || filtersToUse.length === 0) && customFilters.length > 0) {
        const validFilters = customFilters.filter(f => f.column && f.operator && f.value);
        filtersToUse = validFilters;
        if (validFilters.length > 0 && !overrideFilters) {
          setAppliedFilters(validFilters);
        }
      }

      // Fetch grid configuration first
      const config = await fetchGridConfig(viewName);
      setGridConfig(config);
      
      // Generate columns from configuration if available
      if (config) {
        const newColumns = generateColumnsFromConfig(config);
        setColumns(newColumns);
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());

      if (dateRange.start) {
        params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      }
      if (dateRange.end) {
        params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      }

      if (filtersToUse && filtersToUse.length > 0) {
        // Filter out value for operators that don't need it
        const processedFilters = expandTimestampDateFilters(
          filtersToUse.map(filter => {
          const { operator } = filter;
          // List of operators that don't require a value
          const noValueOperators = ['isEmpty', 'isNotEmpty'];
          
          if (noValueOperators.includes(operator)) {
            const { value, ...rest } = filter;
            return rest;
          }
          return filter;
        })
        );
        
        params.append('filter', JSON.stringify(processedFilters));
      }

      // Add view_name from config if available
      if (config && config.category) {
        params.append('view_name', config.category);
      }

      const propertyIdForApi =
        overridePropertyId !== undefined ? overridePropertyId : appliedPropertyId;
      const scope = applyPropertyScope(params, propertyIdForApi, assignedCodes);

      // Fetch report data
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegrationId}/normalized-chargebacks/?${params.toString()}`;
      console.log('Fetching report data:', apiUrl);

      const response = await fetch(apiUrl, {
        method: scope.method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegrationId
        },
        body: scope.body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch normalized chargebacks: ${errorText}`);
      }

      const data = await response.json();
      console.log('Report data received:', data);
      
      // Handle different response structures
      let transactionData = [];
      
      if (data && data.data && Array.isArray(data.data)) {
        transactionData = data.data;
        setTotalCount(
          data.pagination?.total ?? data.total ?? data.count ?? transactionData.length
        );
      } else if (Array.isArray(data)) {
        transactionData = data;
        setTotalCount(data.length);
      } else if (data && typeof data === 'object') {
        if (data.data) {
          transactionData = Array.isArray(data.data) ? data.data : [data.data];
          setTotalCount(data.total || data.count || transactionData.length);
        } else {
          const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
          if (arrayKey) {
            transactionData = data[arrayKey];
            setTotalCount(data.total || data.count || transactionData.length);
          } else {
            transactionData = [data];
            setTotalCount(1);
          }
        }
      }
      
      // Add IDs if missing and ensure all fields from config are included
      const dataWithIds = transactionData.map((item, index) => {
        const newItem = { ...item };
        
        if (config?.grid?.columns) {
          config.grid.columns.forEach(col => {
            if (newItem[col.name] === undefined) {
              newItem[col.name] = null;
            }
          });
        }
        
        newItem.id = item.id || item._id || `row-${index}`;
        
        return newItem;
      });

      const mergedRows = applyPendingPatchesToRows(dataWithIds);
      
      console.log('Processed transaction data:', mergedRows);
      setRows(mergedRows);
      
      // Set hasMore based on received count
      const receivedCount = transactionData.length;
      setHasMore(receivedCount === pageSize);
      
      // Update totalCount if not provided by backend
      if (!data.total && !data.count) {
        setTotalCount((currentPage - 1) * pageSize + receivedCount);
      }
      
      if (!silent) {
        setSnackbar({
          open: true,
          message: 'Normalized Chargebacks loaded successfully',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      // Never wipe the grid on a silent post-action sync — keep optimistic Rejected/Revert state.
      if (!silent) {
        setSnackbar({
          open: true,
          message: `Failed to load report: ${error.message}`,
          severity: 'error',
        });
        setRows([]);
        setColumns([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Custom filter helper functions
  const addCustomFilter = () => {
    const newFilter = {
      id: Date.now(),
      column: '',
      operator: '=',
      value: ''
    };
    setCustomFilters([...customFilters, newFilter]);
  };

  const updateCustomFilter = (id, field, value) => {
    setCustomFilters(filters => 
      filters.map(filter => 
        filter.id === id ? { ...filter, [field]: value } : filter
      )
    );
    
    // Clear applied filters when making changes to ensure we don't use stale data
    setAppliedFilters([]);
  };

  const removeCustomFilter = (id) => {
    const updatedFilters = customFilters.filter(filter => filter.id !== id);
    setCustomFilters(updatedFilters);
    
    const updatedAppliedFilters = appliedFilters.filter(filter => filter.id !== id);
    setAppliedFilters(updatedAppliedFilters);
    
    setCurrentPage(1);
    fetchData();
  };

  const clearCustomFilters = (clearDateRange = false) => {
    setCustomFilters([]);
    setAppliedFilters([]);
    setAppliedPropertyId(propertyLockedFromHub ? mdhNav.propertyCode : null);
    if (!propertyLockedFromHub) {
      setSelectedProperty(null);
      localStorage.removeItem(NORMALIZED_CHARGEBACKS_PROPERTY_STORAGE_KEY);
    }
    if (clearDateRange) {
      setDateRange({ start: null, end: null });
    }
    setSelectedFilter('');
    
    setSnackbar({
      open: true,
      message: 'Filters cleared. Click "Load Data" to refresh with no filters.',
      severity: 'info',
      autoHideDuration: 5000
    });
  };

  const clearAllFilters = () => {
    clearCustomFilters(true); // Clear both date range and other filters
  };

  // Handle download report as CSV
  const handleDownloadReport = async () => {
    if (!canExport) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      if (!tenantIntegrationId) {
        throw new Error('Tenant integration ID not found');
      }

      // Get the view name from grid config or use a default
      const viewName = gridConfig?.category || 'NORMALIZED_CHARGEBACKS';

      // Build query parameters for CSV download
      const params = new URLSearchParams({
        format: 'csv',
        view_name: viewName  // Add the required view_name parameter
      });

      if (dateRange.start) {
        params.append('start_date', format(dateRange.start, 'yyyy-MM-dd'));
      }
      if (dateRange.end) {
        params.append('end_date', format(dateRange.end, 'yyyy-MM-dd'));
      }

      if (appliedFilters.length > 0) {
        params.append('filter', JSON.stringify(expandTimestampDateFilters(appliedFilters)));
      }

      const scope = applyPropertyScope(params, appliedPropertyId, assignedCodes);

      // Match on-screen grid columns; omit Actions + hidden Harmonate metadata fields
      const exportColumns = (columns || [])
        .filter((col) => col.field && col.field !== 'actions')
        .map((col) => ({
          field: col.field,
          title: col.headerName || col.field,
        }));
      if (exportColumns.length > 0) {
        params.append('export_columns', JSON.stringify(exportColumns));
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegrationId}/normalized-chargebacks/?${params.toString()}`;
      console.log('Downloading report:', apiUrl);

      const response = await fetch(apiUrl, {
        method: scope.method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegrationId
        },
        body: scope.body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to download report: ${errorText}`);
      }

      const csvData = await response.text();
      
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `normalized_chargebacks_${timestamp}.csv`;
      link.setAttribute('download', filename);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Normalized Chargebacks downloaded successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      setSnackbar({
        open: true,
        message: `Failed to download report: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  // Load selected filter configuration into UI
  const loadFilter = (filter) => {
    try {
      // Parse the filter data
      const filterData = typeof filter.filter === 'string' ? JSON.parse(filter.filter) : filter.filter;
      
      // Set date range if it exists in the saved filter
      if (filterData.dateRange) {
        setDateRange(filterData.dateRange);
      } else {
        setDateRange({ start: null, end: null });
      }
      
      // Set custom filters if they exist
      if (filterData.customFilters?.length > 0) {
        const validFilters = filterData.customFilters.filter(f => f.column && f.operator && f.value);
        setCustomFilters(validFilters);
        // Also update appliedFilters so they're used when Load Data is clicked
        setAppliedFilters(validFilters);
        // Show the custom filters section when loading a saved filter
        setShowCustomFilters(true);
      } else {
        setCustomFilters([]);
        setAppliedFilters([]);
      }
      
      setSelectedFilter(filter.id);
      
      setSnackbar({
        open: true,
        message: `Filter '${filter.filterName}' loaded. Click 'Load Data' to apply.`,
        severity: 'info',
        autoHideDuration: 5000
      });
      
      // Don't automatically fetch data when loading a filter
      setCurrentPage(1);
      setHasMore(true);
    } catch (error) {
      console.error('Error loading filter:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load filter',
        severity: 'error'
      });
    }
  };
  

  // Export data to Excel
  const exportToExcel = (data, filename) => {
    if (!canExport) return;
    const exportData = data.map(row => {
      const exportRow = {};
      columns.forEach(col => {
        const value = row[col.field];
        
        if (value === null || value === undefined) {
          exportRow[col.headerName] = '';
        } else if (col.type === 'date' || col.field.toLowerCase().includes('date')) {
          try {
            exportRow[col.headerName] = format(new Date(value), 'MMM dd, yyyy');
          } catch {
            exportRow[col.headerName] = value;
          }
        } else if (col.type === 'number' || typeof value === 'number') {
          exportRow[col.headerName] = value;
        } else {
          exportRow[col.headerName] = value;
        }
      });
      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    const columnWidths = columns.map(col => ({
      wch: Math.max(col.headerName.length, 15)
    }));
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    const sheetName = 'Normalized Chargebacks';
    
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));

    const fileName = `${sheetName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    
    setSnackbar({
      open: true,
      message: 'Excel file downloaded successfully',
      severity: 'success',
    });
  };
  
  // Toggle filters panel
  const toggleFilters = () => {
    setShowCustomFilters(!showCustomFilters);
  };

  // Save current filter configuration
  const saveFilter = async () => {
    if (!filterName.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a filter name',
        severity: 'warning'
      });
      return;
    }
    
    try {
      setSavingFilter(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/chargeback-headers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tenantId: tenantId,
          report: 'NORMALIZED_CHARGEBACKS',
          filterName: filterName.trim(),
          filter: JSON.stringify({
            dateRange,
            customFilters: customFilters.filter(f => f.column && f.operator && f.value),
          })
        })
      });

      if (response.ok) {
        const newFilter = await response.json();
        setSavedFilters(prev => [...prev, newFilter]);
        setFilterName('');
        setSaveDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'Filter saved successfully',
          severity: 'success'
        });
        loadSavedFilters(); // Refresh the saved filters list
      } else {
        throw new Error('Failed to save filter');
      }
    } catch (error) {
      console.error('Error saving filter:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save filter',
        severity: 'error'
      });
    } finally {
      setSavingFilter(false);
    }
  };

  // Handle load data (reset to page 1 and trigger data fetch)
  const handleLoadData = async () => {
    // First, update appliedFilters with current custom filters
    const validFilters = customFilters.filter(f => f.column && f.operator && f.value);

    const propertyIdForApi = propertyLockedFromHub
      ? mdhNav.propertyCode
      : selectedProperty
        ? propertyCodeForApi(selectedProperty)
        : null;

    // Keep applied filters in sync for subsequent pagination or reloads
    setAppliedFilters(validFilters);
    setAppliedPropertyId(propertyIdForApi);

    // Trigger the data fetch immediately with the freshest filters to avoid
    // relying on async state updates.
    setCurrentPage(1);
    setHasMore(true);
    fetchData(validFilters, propertyIdForApi);
  };

  // Handle previous page
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Handle next page
  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Box
        sx={{
          ...harmonatePageSx,
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        height: '100vh',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Dialog
        open={saveDialogOpen}
        onClose={() => {
          setSaveDialogOpen(false);
          setFilterName('');
          setSelectedFilter('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {selectedFilter ? 'Update Filter' : 'Save Current Filter'}
        </DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <TextField
            autoFocus
            margin="dense"
            label="Filter Name"
            type="text"
            fullWidth
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            disabled={savingFilter}
            sx={{ ...harmonateFormFieldsSx, ...harmonateTextFieldSx }}
            InputLabelProps={harmonateInputLabelProps}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setSaveDialogOpen(false);
              setFilterName('');
              setSelectedFilter('');
            }}
            disabled={savingFilter}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          {selectedFilter ? (
            <>
              <Button
                onClick={() => handleSaveFilter(true)}
                variant="contained"
                disabled={!filterName.trim() || savingFilter}
                startIcon={savingFilter ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={harmonateProceedButtonSx}
              >
                {savingFilter ? 'Updating...' : 'Update'}
              </Button>
              <Button
                onClick={() => {
                  setSelectedFilter('');
                  handleSaveFilter(false);
                }}
                variant="outlined"
                disabled={!filterName.trim() || savingFilter}
                startIcon={<SaveAsIcon />}
                sx={harmonateUploadOutlinedSx}
              >
                Save as New
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleSaveFilter(false)}
              variant="contained"
              disabled={!filterName.trim() || savingFilter}
              startIcon={savingFilter ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={harmonateProceedButtonSx}
            >
              {savingFilter ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle id="alert-dialog-title" sx={harmonateDialogTitleSx}>
          Delete Filter
        </DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <DialogContentText id="alert-dialog-description" sx={{ color: ht.text }}>
            Are you sure you want to delete this filter? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteClose} variant="outlined" sx={harmonateCancelButtonSx}>
            No, Keep It
          </Button>
          <Button onClick={handleDeleteFilter} variant="contained" autoFocus sx={harmonateAbortButtonSx}>
            Yes, Delete It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Don't Fight confirmation + note */}
      <Dialog
        open={dontFightDialogOpen}
        onClose={closeDontFightDialog}
        aria-labelledby="dont-fight-dialog-title"
        PaperProps={harmonateDialogPaperProps}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="dont-fight-dialog-title" sx={harmonateDialogTitleSx}>
          Confirm Don&apos;t Fight
        </DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <DialogContentText sx={{ color: ht.text, mb: 2 }}>
            This will move the chargeback to the Rejected stage. Confirm you do not want to
            fight this chargeback.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Note"
            placeholder="Briefly explain why no action is being taken (optional)"
            value={dontFightNote}
            onChange={(e) => setDontFightNote(e.target.value)}
            disabled={dontFightSubmitting}
            inputProps={{ maxLength: 2000 }}
            sx={harmonateTextFieldSx}
            InputLabelProps={harmonateInputLabelProps}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeDontFightDialog}
            variant="outlined"
            disabled={dontFightSubmitting}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDontFight}
            variant="contained"
            disabled={dontFightSubmitting}
            sx={harmonateProceedButtonSx}
          >
            {dontFightSubmitting ? 'Saving…' : "Confirm Don't Fight"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert from Rejected */}
      <Dialog
        open={revertDialogOpen}
        onClose={closeRevertDialog}
        aria-labelledby="revert-dont-fight-dialog-title"
        PaperProps={harmonateDialogPaperProps}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="revert-dont-fight-dialog-title" sx={harmonateDialogTitleSx}>
          Revert to Main List
        </DialogTitle>
        <DialogContent sx={harmonateDialogFormContentSx}>
          <DialogContentText sx={{ color: ht.text }}>
            This chargeback was marked Don&apos;t Fight and is in Rejected. Revert it back to
            the main list so it can be worked again?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeRevertDialog}
            variant="outlined"
            disabled={revertSubmitting}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRevertDontFight}
            variant="contained"
            disabled={revertSubmitting}
            sx={harmonateProceedButtonSx}
          >
            {revertSubmitting ? 'Reverting…' : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header with Actions */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        {mdhNav.fromHub && (
          <MerchantDataHubBreadcrumb
            currentLabel="Chargeback Management"
            propertyLabel={
              propertyLockedFromHub
                ? formatMdhPropertyLabel(mdhNav.propertyCode, mdhNav.propertyName)
                : null
            }
          />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" component="h1" sx={harmonatePageTitleSx}>
            Chargeback Management
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'flex-end',
            }}
          >
            {!mdhNav.fromHub && (
            <Autocomplete
              size="small"
              options={properties}
              value={selectedProperty}
              onChange={handlePropertySelectChange}
              getOptionLabel={(option) => {
                const code = option.propertyCode || option.PROPERTY_CODE || '';
                const name = option.propertyName || option.PROPERTY_NAME || '';
                return code && name ? `${code} - ${name}` : name || code || '';
              }}
              isOptionEqualToValue={(option, value) =>
                String(getTenantPropertyOptionId(option) ?? '') ===
                String(getTenantPropertyOptionId(value) ?? '')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property Select"
                  placeholder="Select property"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {propertiesLoading ? (
                          <CircularProgress color="inherit" size={18} sx={{ color: ht.white }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{
                ...harmonateToolbarBuFieldSx,
                minWidth: { xs: '100%', sm: 280 },
                maxWidth: 360,
                width: 280,
                '& .MuiAutocomplete-popupIndicator': { color: ht.white },
                '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.75)' },
                '& .MuiAutocomplete-endAdornment': { right: 6 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'transparent',
                  borderRadius: '8px !important',
                  height: 40,
                  alignItems: 'center',
                  color: ht.white,
                  paddingRight: '36px !important',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.55)',
                    borderRadius: '8px',
                  },
                  '&:hover fieldset': { borderColor: ht.white },
                  '&.Mui-focused fieldset': { borderColor: ht.white },
                },
                '& .MuiOutlinedInput-input': {
                  px: 1.25,
                  fontSize: '0.875rem',
                  color: ht.white,
                  py: 0,
                },
              }}
              disabled={propertiesLoading}
            />
            )}
            <Tooltip title={canExport ? (downloading ? 'Downloading...' : 'Download CSV') : 'No export permission'}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownloadReport}
                  disabled={downloading || !canExport}
                  sx={harmonateToolbarButtonSx}
                >
                  {downloading ? 'Downloading...' : 'Download CSV'}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={toggleFilters}
              sx={showCustomFilters ? { ...harmonateProceedButtonSx, height: 40 } : harmonateToolbarButtonSx}
            >
              {showCustomFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {/* Action Menu */}
            <Menu
              id="action-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: ht.fieldRadius,
                    '& .MuiMenuItem-root': { fontSize: '0.875rem', color: ht.text },
                  },
                },
              }}
            >
              {selectedRowIsRejected ? (
                <MenuItem onClick={() => handleMenuItemClick('revert')}>
                  Revert to Main
                </MenuItem>
              ) : selectedRowIsExpired ? (
                <MenuItem disabled>Expired</MenuItem>
              ) : (
                [
                  <MenuItem key="fight" onClick={() => handleMenuItemClick('fight')}>
                    Fight Transaction
                  </MenuItem>,
                  <MenuItem key="noAction" onClick={() => handleMenuItemClick('noAction')}>
                    Don&apos;t Fight
                  </MenuItem>,
                ]
              )}
            </Menu>
          </Box>
        </Box>
        
      </Box>

      {/* Custom Filter Interface */}
      {showCustomFilters && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Paper sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, mb: 2, border: `1px solid ${ht.line}` }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={harmonateSectionTitleSx}>
              Custom Filters
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
              {/* Left side - Add/Apply/Clear Filters */}
              <Box display="flex" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={addCustomFilter}
                  sx={{ ...harmonateUploadOutlinedSx, fontSize: '0.75rem', py: 0.5 }}
                >
                  Add Filter
                </Button>
               
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearCustomFilters}
                  sx={harmonateUploadOutlinedSx}
                >
                  Clear Filter
                </Button>
              </Box>
              {/* Right side - Save/Load Filters */}
              <Box display="flex" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenSaveDialog}
                  startIcon={<SaveIcon fontSize="small" />}
                  disabled={customFilters.length === 0 && !(dateRange.start || dateRange.end)}
                  sx={{
                    ...harmonateUploadOutlinedSx,
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: '-4px',
                    },
                  }}
                >
                  Save Filter
                </Button>
               
                <FormControl size="small" sx={{ minWidth: 180, ...harmonateFormFieldsSx }}>
                  <InputLabel>Select a filter</InputLabel>
                  <Select
                    value={selectedFilter || ''}
                    onChange={(e) => {
                      if (!e.target.value) {
                        applySavedFilter(null);
                      } else {
                        // Find the full filter object to pass to applySavedFilter
                        const selected = savedFilters.find(f => f.id === e.target.value);
                        applySavedFilter(selected);
                      }
                    }}
                    disabled={savedFilters.length === 0}
                    displayEmpty
                    size="small"
                    label="Select a filter"
                    MenuProps={harmonateSelectMenuProps}
                    renderValue={(selected) => {
                      if (!selected) return '';
                      const filter = typeof selected === 'object' ? selected : savedFilters.find(f => f.id === selected);
                      return filter?.filterName || '';
                    }}
                  >
                    <MuiMenuItem value="">
                      <em>Select a filter</em>
                    </MuiMenuItem>
                    {savedFilters.map((filter) => (
                      <MuiMenuItem
                        key={filter.id}
                        value={filter.id}
                      >
                        {filter.filterName}
                      </MuiMenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Delete selected filter">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const filterId = selectedFilter?.id || selectedFilter;
                      if (filterId) {
                        handleDeleteClick(filterId);
                      }
                    }}
                    disabled={!selectedFilter || savedFilters.length === 0}
                    sx={{
                      ...harmonateGridActionIconSx,
                      '&:hover': { color: ht.red, bgcolor: 'rgba(239, 63, 63, 0.08)' },
                      visibility: selectedFilter ? 'visible' : 'hidden',
                      marginLeft: '-8px',
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          {/* Date Range Picker */}
          <Box mb={3} sx={harmonateFormFieldsSx}>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                label="From"
                type="date"
                value={dateRange.start?.toISOString().split('T')[0] || ''}
                onChange={(e) => setDateRange(prev => ({
                  ...prev,
                  start: e.target.value ? new Date(e.target.value) : null
                }))}
                InputLabelProps={{ shrink: true, ...harmonateInputLabelProps }}
                sx={{ width: '160px', ...harmonateTextFieldSx }}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                value={dateRange.end?.toISOString().split('T')[0] || ''}
                onChange={(e) => setDateRange(prev => ({
                  ...prev,
                  end: e.target.value ? new Date(e.target.value) : null
                }))}
                InputLabelProps={{ shrink: true, ...harmonateInputLabelProps }}
                sx={{ width: '160px', ...harmonateTextFieldSx }}
              />
            </Box>
          </Box>
          {customFilters.length > 0 && (
            <Box display="flex" flexDirection="column" gap={2} sx={harmonateFormFieldsSx}>
              {customFilters.map((filter, index) => (
                <Card key={filter.id} variant="outlined" sx={{ borderColor: ht.line }}>
                  <CardContent sx={{ py: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Column</InputLabel>
                          <Select
                            value={filter.column}
                            onChange={(e) => updateCustomFilter(filter.id, 'column', e.target.value)}
                            label="Column"
                            MenuProps={harmonateSelectMenuProps}
                          >
                            {getAvailableColumns().map((col) => (
                              <MuiMenuItem key={col.value} value={col.value}>
                                {col.label}
                              </MuiMenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                     
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={filter.operator}
                            onChange={(e) => updateCustomFilter(filter.id, 'operator', e.target.value)}
                            label="Operator"
                            MenuProps={harmonateSelectMenuProps}
                          >
                            {FILTER_OPERATORS.map((op) => (
                              <MuiMenuItem key={op.value} value={op.value}>
                                {op.label}
                              </MuiMenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                     
                      <Grid item xs={12} sm={4}>
                        {(() => {
                          const selectedColumn = columns.find(col => col.field === filter.column);
                          const isDateColumn = selectedColumn?.type === 'date' ||
                                             (selectedColumn?.field?.toLowerCase().includes('date') ||
                                              selectedColumn?.field?.toLowerCase().includes('time'));
                          
                          // List of operators that don't require a value
                          const noValueOperators = ['isEmpty', 'isNotEmpty'];
                          const requiresValue = !noValueOperators.includes(filter.operator);
                         
                          if (!requiresValue) {
                            return null; // Don't render any input for these operators
                          }
                          
                          if (isDateColumn) {
                            return (
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Date"
                                value={filter.value || ''}
                                onChange={(e) => updateCustomFilter(filter.id, 'value', e.target.value)}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            );
                          }
                         
                          return (
                            <TextField
                              fullWidth
                              size="small"
                              label="Value"
                              value={filter.value || ''}
                              onChange={(e) => updateCustomFilter(filter.id, 'value', e.target.value)}
                              placeholder="Enter filter value"
                            />
                          );
                        })()}
                      </Grid>
                     
                      <Grid item xs={12} sm={2}>
                        <Box display="flex" justifyContent="center">
                          <IconButton
                            onClick={() => removeCustomFilter(filter.id)}
                            size="small"
                            sx={{
                              ...harmonateGridActionIconSx,
                              '&:hover': { color: ht.red, bgcolor: 'rgba(239, 63, 63, 0.08)' },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
      )}

      {/* Lifecycle stage tabs (Payment Intelligence pattern) */}
      <Paper elevation={0} sx={{ borderRadius: 2, mb: 2, boxShadow: '0px 2px 8px rgba(0,0,0,0.05)' }}>
        <Tabs
          value={activeStageTabIndex}
          onChange={(_, index) => {
            const next = CHARGEBACK_STAGE_TABS[index];
            if (next) setStageTab(next.key);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            borderBottom: '1px solid #eee',
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 48,
              fontSize: '0.9rem',
              minWidth: 'auto',
              px: 2.5,
            },
            '& .Mui-selected': { color: '#4a6741', fontWeight: 600 },
            '& .MuiTabs-indicator': { bgcolor: '#8CD867', height: 3 },
          }}
        >
          {CHARGEBACK_STAGE_TABS.map((tab) => (
            <Tab
              key={tab.key}
              label={
                <StageTabLabel
                  text={tab.label}
                  count={stageCounts[tab.key] ?? 0}
                  tone={tab.tone}
                />
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Pagination Controls */}
      <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 2, mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ color: ht.text, fontSize: '0.875rem' }}>
            {loading
              ? 'Loading...'
              : `Page ${currentPage} — ${stageFilteredRows.length} in this stage (${rows.length} loaded)`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={loading || currentPage <= 1}
              onClick={handlePreviousPage}
              startIcon={<ArrowBackIcon />}
              sx={harmonateUploadOutlinedSx}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={handleLoadData}
              sx={harmonateProceedButtonSx}
            >
              Load Data
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={loading || !hasMore}
              onClick={handleNextPage}
              endIcon={<ArrowForwardIcon />}
              sx={harmonateUploadOutlinedSx}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main grid — same Paper treatment as Workflows list */}
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          bgcolor: ht.white,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          mb: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && rows.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200 }}>
            <CircularProgress sx={{ color: ht.text }} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <DataGridPro
              columnResizeMode="onEnd"
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              resizable
              rows={stageFilteredRows}
              columns={displayColumns}
              disableSelectionOnClick
              slots={{
                toolbar: () => (
                  <GridToolbarContainer>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                    {canExport && (
                      <Button
                        startIcon={<GetAppIcon />}
                        onClick={() => exportToExcel(stageFilteredRows, 'normalized_chargebacks')}
                        sx={{ ...harmonateUploadOutlinedSx, ml: 1 }}
                      >
                        Download as Excel
                      </Button>
                    )}
                  </GridToolbarContainer>
                ),
              }}
              sx={{
                ...harmonateDataGridSx,
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Charge;